import type { SupabaseClient } from "@supabase/supabase-js";

// Free-duty Live presence (2026-08-20 lock amend). An officer who clocks in without a route is
// on duty: consent is accepted, punches and pings are captured, and the samples already exist.
// Live CAD simply could not see them, because every duty read on this desk indexes by
// route_assignment_id and drops null keys.
//
// This module is the read side of the fix. It calls ONE hosted RPC,
// public.list_free_duty_live_presence, which does the work the console structurally cannot:
// dedupe null-assignment pings to a latest-per-officer behind a REDACTED key, apply the
// clocked-in and no-active-assignment gates per person, and resolve the containing L0 site from
// geometry rather than from an assignment the officer does not have.
//
// What must never appear here: user_id. The hosted function returns member_user_ref (eight hex
// characters of a hash) and a resolved display label, exactly like management_on_shift_roster.
// A raw auth uuid has no representation in this module and no reader that would supply one.
//
// UNKNOWN IS NOT EMPTY. The RPC refuses a caller without schedule.manage rather than handing
// back an empty list, and this module preserves that distinction by throwing. A desk that
// swallowed the refusal would paint "nobody is on free duty" at an operator who simply is not
// allowed to know, which is the false-zero this codebase keeps having to design against.

/** Namespace for free-duty marker ids so they can never collide with a route assignment id. */
export const FREE_DUTY_MARKER_PREFIX = "free:";

/** Stable marker/cluster id for one free-duty officer. */
export function freeDutyMarkerId(memberRef: string): string {
  return `${FREE_DUTY_MARKER_PREFIX}${memberRef}`;
}

/** True when a Live marker id belongs to the free-duty family rather than an assignment. */
export function isFreeDutyMarkerId(id: string | null | undefined): boolean {
  return typeof id === "string" && id.startsWith(FREE_DUTY_MARKER_PREFIX);
}

export interface FreeDutyPresenceRow {
  /** Redacted per-member key from member_user_ref. Never a user id. */
  memberRef: string;
  guardLabel: string;
  /** Containing L0 site, or null for the Unassigned group. Never guessed. */
  siteId: string | null;
  siteName: string | null;
  lat: number | null;
  lng: number | null;
  gpsAccuracyMeters: number | null;
  dutyCallId: string | null;
  pingSource: string | null;
  deviceTime: string | null;
  serverReceivedAt: string | null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  // numeric columns arrive as strings through PostgREST.
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function mapFreeDutyPresenceRow(raw: Record<string, unknown>): FreeDutyPresenceRow | null {
  const memberRef = asString(raw.member_ref);
  if (memberRef === null) {
    // Without the redacted key there is no person to dedupe or key a marker on, and painting it
    // anyway would put an anonymous dot on CAD that no operator can act on.
    return null;
  }
  return {
    memberRef,
    guardLabel: asString(raw.guard_label) ?? "On-duty officer",
    siteId: asString(raw.site_id),
    siteName: asString(raw.site_name),
    lat: asNumber(raw.lat),
    lng: asNumber(raw.lng),
    gpsAccuracyMeters: asNumber(raw.gps_accuracy_meters),
    dutyCallId: asString(raw.duty_call_id),
    pingSource: asString(raw.ping_source),
    deviceTime: asString(raw.device_time),
    serverReceivedAt: asString(raw.server_received_at),
  };
}

/**
 * Officers on duty with no active route assignment and an ordinarily recent ping. Explicit
 * redacted member refs may recover retained coordinates for the existing filtered keep-visible
 * exception. Throws on refusal or a dead read so the caller holds "unknown", not false emptiness.
 */
export async function fetchFreeDutyLivePresence(
  client: SupabaseClient,
  accountId: string,
  keepVisibleMemberRefs: readonly string[] = [],
): Promise<FreeDutyPresenceRow[]> {
  const { data, error } = await client.rpc("list_free_duty_live_presence", {
    active_account_id: accountId,
    p_keep_visible_member_refs: [...keepVisibleMemberRefs],
  });
  if (error !== null) {
    throw new Error("Could not load free-duty presence.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  const rows: FreeDutyPresenceRow[] = [];
  for (const raw of data as Array<Record<string, unknown>>) {
    const row = mapFreeDutyPresenceRow(raw);
    if (row !== null) {
      rows.push(row);
    }
  }
  return rows;
}

/** Last-ping timestamp a freshness calculation should read, device time as the fallback. */
export function freeDutyLastPingIso(row: FreeDutyPresenceRow): string | null {
  return row.serverReceivedAt ?? row.deviceTime;
}

/** Rows that can actually be drawn. A row without coordinates is presence we cannot place. */
export function freeDutyRowsWithCoordinates(
  rows: readonly FreeDutyPresenceRow[],
): FreeDutyPresenceRow[] {
  return rows.filter((row) => row.lat !== null && row.lng !== null);
}
